import { formatSignedUsd, truncateDecimalAmount } from './numberFormat';

export interface ParsedTradeDetails {
  cex?: {
    venue: string;
    side: 'buy' | 'sell';
    token: string;
    quoteToken: 'USDT';
    spentToken: string;
    spentAmount: string;
    receivedToken: string;
    receivedAmount: string;
    feeToken: string | null;
    feeAmount: string | null;
    avgPrice: number | null;
    orderId: string | null;
    status: string | null;
  };
  route?: {
    mode: string;
    executionMode: string;
    safetyMode: string;
    partialUnwindEnabled: boolean;
    partialUnwindSlippage: number;
    partialUnwindMaxLossPct: number;
    dexProvider: string;
    amountUsd: number;
    slippage: number;
    thresholdPct: number;
    expectedSpreadPct: number | null;
    expectedProfitUsd: number | null;
  };
  dex?: {
    provider: string;
    side: 'buy' | 'sell';
    spentToken: string;
    spentAmount: string;
    actualSpentAmount?: string | null;
    refundedAmount?: string | null;
    receivedToken: string;
    expectedReceivedAmount: string;
    receivedAmount?: string | null;
    minReceivedAmount: string | null;
    txHash: string | null;
    executed?: boolean;
    confirmationReason?: string | null;
    balanceSnapshotTimeMs?: number | null;
    submitTimeMs?: number | null;
    confirmTimeMs?: number | null;
    totalTimeMs?: number | null;
  };
  totals?: {
    token: string;
    tokenDelta: string;
    usdtDelta: string;
    referencePriceUsdt: number | null;
    estimatedValueDeltaUsdt: string | null;
    cexFeeToken: string | null;
    cexFeeAmount: string | null;
    tonGasUsdt: string | null;
    note: string | null;
  };
  diagnostics?: {
    cexRequestedSide: 'buy' | 'sell';
    cexRequestedAmount: string | null;
    cexRequestedToken: string;
    cexExpectedQuoteAmount: string | null;
    cexExpectedAvgPrice: number | null;
    cexActualAvgPrice: number | null;
    cexAvgPriceDriftPct: number | null;
    cexFillRatioPct: number | null;
    cexDepthUsd: number | null;
    cexLevelsUsed: number | null;
    cexVwapImpactPct: number | null;
    dexQuoteAgeMs: number | null;
    dexQuoteTtlMs: number | null;
    dexFillRatioPct: number | null;
    dexPriceImpactPct: number | null;
    dexRoute: string[];
    dexGasBudget: string | null;
  };
  recovery?: {
    status: 'disabled' | 'blocked' | 'completed' | 'partial' | 'failed';
    originToken: string;
    originalAmount: string;
    slippage: number;
    maxLossPct: number;
    traceComplete: boolean;
    traceId: string | null;
    assets: Array<{ token: string; amount: string; source: 'output' | 'refund' | 'intermediate' }>;
    legs: Array<{
      tokenIn: string;
      tokenOut: string;
      amountIn: string;
      expectedAmountOut: string;
      actualAmountOut: string | null;
      provider: string;
      txHash: string | null;
      confirmed: boolean;
      reason: string | null;
    }>;
    expectedRecoveredAmount: string | null;
    recoveredAmount: string | null;
    lossAmount: string | null;
    lossUsd: string | null;
    tonGasAmount: string | null;
    tonGasUsdt: string | null;
    reason: string | null;
  };
}

export function parseTradeDetails(trade: any): ParsedTradeDetails | null {
  if (!trade?.detailsJson) return null;
  try {
    return JSON.parse(trade.detailsJson) as ParsedTradeDetails;
  } catch {
    return null;
  }
}

function num(value: string | number | null | undefined): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatAmount(value: string | number | null | undefined, decimals = 8): string {
  const parsed = num(value);
  if (parsed === null) return '-';
  return truncateDecimalAmount(parsed, Math.min(decimals, 2));
}

export function formatSignedAmount(value: string | number | null | undefined, token: string): string {
  const parsed = num(value);
  if (parsed === null) return '-';
  const sign = parsed > 0 ? '+' : parsed < 0 ? '-' : '';
  return sign + formatAmount(Math.abs(parsed), token === 'USDT' ? 6 : 8) + ' ' + token;
}

export function formatUsdValue(value: string | number | null | undefined): string {
  const formatted = formatSignedUsd(value);
  return formatted === 'N/A' ? '-' : formatted;
}

export function formatPrice(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '-';
  return formatAmount(value, 8) + ' USDT';
}

export function formatDurationMs(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '-';
  if (value < 1000) return Math.max(0, Math.trunc(value)) + 'мс';
  return truncateDecimalAmount(value / 1000, 1) + 'с';
}

export function formatDexSubmitTime(details: ParsedTradeDetails | null | undefined): string {
  return formatDurationMs(details?.dex?.submitTimeMs);
}

export function formatDexConfirmTime(details: ParsedTradeDetails | null | undefined): string {
  return formatDurationMs(details?.dex?.confirmTimeMs);
}

export function formatDexTotalTime(details: ParsedTradeDetails | null | undefined, fallbackMs?: number | null): string {
  return formatDurationMs(details?.dex?.totalTimeMs ?? fallbackMs);
}

export function formatDexTiming(details: ParsedTradeDetails | null | undefined, fallbackMs?: number | null): string {
  const parts: string[] = [];
  if (details?.dex?.balanceSnapshotTimeMs !== undefined && details.dex.balanceSnapshotTimeMs !== null) {
    parts.push('bal ' + formatDurationMs(details.dex.balanceSnapshotTimeMs));
  }
  if (details?.dex?.submitTimeMs !== undefined && details.dex.submitTimeMs !== null) {
    parts.push('send ' + formatDurationMs(details.dex.submitTimeMs));
  }
  if (details?.dex?.confirmTimeMs !== undefined && details.dex.confirmTimeMs !== null) {
    parts.push('confirm ' + formatDurationMs(details.dex.confirmTimeMs));
  }
  const total = details?.dex?.totalTimeMs ?? fallbackMs;
  if (total !== undefined && total !== null) parts.push('total ' + formatDurationMs(total));
  return parts.length > 0 ? parts.join(' / ') : '-';
}

export function formatCexSummary(details: ParsedTradeDetails): string | null {
  if (!details.cex) return null;
  const c = details.cex;
  const price = c.avgPrice !== null ? ' по ' + formatPrice(c.avgPrice) : '';
  const fee = c.feeAmount && c.feeToken ? ', fee ' + formatAmount(c.feeAmount) + ' ' + c.feeToken : '';
  return formatAmount(c.receivedAmount) + ' ' + c.receivedToken
    + ' за ' + formatAmount(c.spentAmount) + ' ' + c.spentToken
    + price + fee;
}

export function formatDexSummary(details: ParsedTradeDetails): string | null {
  if (!details.dex) return null;
  const d = details.dex;
  if (d.executed === false && d.refundedAmount && !d.receivedAmount) {
    const reason = d.confirmationReason ? ' | ' + d.confirmationReason : '';
    const timing = d.submitTimeMs !== undefined || d.confirmTimeMs !== undefined
      ? ' | send ' + formatDurationMs(d.submitTimeMs) + ' / confirm ' + formatDurationMs(d.confirmTimeMs)
      : '';
    return 'refund: ' + formatAmount(d.refundedAmount) + ' ' + d.spentToken
      + ' вернулось | ' + d.provider + timing + reason;
  }
  const hasPartialFill = d.executed === false && (!!d.receivedAmount || !!d.actualSpentAmount);
  const prefix = d.executed === false ? (hasPartialFill ? 'частично: ' : 'не исполнено: ') : '';
  const receiveLabel = d.executed === false && !hasPartialFill ? 'ожидалось ~' : 'за ~';
  const spentAmount = d.actualSpentAmount || d.spentAmount;
  const receivedAmount = d.executed === false && !hasPartialFill ? d.expectedReceivedAmount : (d.receivedAmount || d.expectedReceivedAmount);
  const min = d.minReceivedAmount ? ' | ожидали ' + formatAmount(d.minReceivedAmount) + ' ' + d.receivedToken : '';
  const reason = d.executed === false && d.confirmationReason ? ' | ' + d.confirmationReason : '';
  const timing = d.submitTimeMs !== undefined || d.confirmTimeMs !== undefined
    ? ' | send ' + formatDurationMs(d.submitTimeMs) + ' / confirm ' + formatDurationMs(d.confirmTimeMs)
    : '';
  return prefix + formatAmount(spentAmount) + ' ' + d.spentToken
    + ' ' + receiveLabel + formatAmount(receivedAmount) + ' ' + d.receivedToken
    + ' | ' + d.provider + min + timing + reason;
}

export function formatTotalsSummary(details: ParsedTradeDetails): string | null {
  if (!details.totals) return null;
  const t = details.totals;
  const value = t.estimatedValueDeltaUsdt !== null ? ', итог ' + formatUsdValue(t.estimatedValueDeltaUsdt) : '';
  return 'Δ ' + formatSignedAmount(t.tokenDelta, t.token)
    + ' · Δ ' + formatSignedAmount(t.usdtDelta, 'USDT')
    + value;
}

export function formatRecoverySummary(details: ParsedTradeDetails | null | undefined): string | null {
  const recovery = details?.recovery;
  if (!recovery || recovery.status === 'disabled') return null;
  if (recovery.status === 'blocked' || recovery.status === 'failed') {
    return (recovery.status === 'blocked' ? 'заблокирован: ' : 'ошибка: ') + (recovery.reason || '-');
  }

  const legs = recovery.legs.map(leg => {
    const out = leg.actualAmountOut || leg.expectedAmountOut;
    return formatAmount(leg.amountIn) + ' ' + leg.tokenIn + ' -> '
      + formatAmount(out) + ' ' + leg.tokenOut + ' | ' + leg.provider;
  });
  const result = recovery.recoveredAmount
    ? 'вернули ' + formatAmount(recovery.recoveredAmount) + ' ' + recovery.originToken
    : '';
  const loss = recovery.lossUsd !== null ? 'убыток ' + formatAmount(recovery.lossUsd) + ' USDT' : '';
  const gas = recovery.tonGasAmount !== null ? 'gas ' + formatAmount(recovery.tonGasAmount) + ' TON' : '';
  return [recovery.status === 'completed' ? 'возвращено' : 'частичный возврат', ...legs, result, loss, gas]
    .filter(Boolean)
    .join(' · ');
}

function formatPlainPct(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '-';
  const sign = value > 0 ? '+' : '';
  return sign + formatAmount(value, 4) + '%';
}

export function formatDiagnosticsSummary(details: ParsedTradeDetails | null | undefined): string | null {
  const d = details?.diagnostics;
  if (!d) return null;

  const parts = [
    'CEX fill ' + formatPlainPct(d.cexFillRatioPct),
    'drift ' + formatPlainPct(d.cexAvgPriceDriftPct),
    'DEX fill ' + formatPlainPct(d.dexFillRatioPct),
    'quote ' + formatDurationMs(d.dexQuoteAgeMs),
  ];

  if (d.cexVwapImpactPct !== null && d.cexVwapImpactPct !== undefined) {
    parts.push('CEX imp ' + formatPlainPct(d.cexVwapImpactPct));
  }
  if (d.dexPriceImpactPct !== null && d.dexPriceImpactPct !== undefined) {
    parts.push('DEX imp ' + formatPlainPct(d.dexPriceImpactPct));
  }
  return parts.join(' · ');
}
